import { create } from "zustand"

import { TaskViewScope } from "../constants"

import type {
  GetSourceChangeApprovalListQuery,
  SourceChangeApprovalListInput,
  SourceChangeApprovalProduct,
} from "@/graphql/codegen/graphql"
import { GetSourceChangeApprovalListDocument } from "@/graphql/codegen/graphql"
import type { SearchFilters } from "@/stores/createSearchStore"
import { createSearchStore } from "@/stores/createSearchStore"

export interface SourceChangeApprovalSearchFilters extends SourceChangeApprovalListInput, SearchFilters {
  taskViewScope?: TaskViewScope
}

interface SourceChangeApprovalQueryInput {
  offset: number
  limit: number
  isAssignedToMe: boolean
}

const deduplicateSourceChangeApprovals = (
  newData: SourceChangeApprovalProduct[],
  existingData: SourceChangeApprovalProduct[]
): SourceChangeApprovalProduct[] => {
  const uniqueIds = new Set(existingData.map((sourceChangeApproval) => sourceChangeApproval.productId))
  return [...existingData, ...newData.filter((sourceChangeApproval) => !uniqueIds.has(sourceChangeApproval.productId))]
}

export const useSourceChangeSearchStore = create(
  createSearchStore<SourceChangeApprovalProduct, SourceChangeApprovalSearchFilters, GetSourceChangeApprovalListQuery>({
    documentNode: GetSourceChangeApprovalListDocument,
    deduplicateData: deduplicateSourceChangeApprovals,
    getQueryInput: (
      filters: SourceChangeApprovalSearchFilters,
      offset: number,
      limit: number
    ): SourceChangeApprovalQueryInput => ({
      offset,
      limit,
      isAssignedToMe: filters.taskViewScope === TaskViewScope.MyTasks,
    }),
    getQueryVariables: ({ isAssignedToMe, ...input }: SourceChangeApprovalSearchFilters) => ({
      input,
      isAssignedToMe,
    }),
    extractDataFromResponse: (data) => {
      const output = data.dashboard.mySourceChangeApprovalList ?? data.dashboard.sourceChangeApprovalList
      return {
        __typename: output?.__typename ?? "SourceChangeApprovalListFailure",
        data:
          output?.__typename === "SourceChangeApprovalListSuccess"
            ? (output.products as SourceChangeApprovalProduct[])
            : [],
        error: output?.__typename === "SourceChangeApprovalListFailure" ? output.error : undefined,
      }
    },
  })
)
